import { useMemo } from 'react';

import { calculateDuration } from '@/utils/calculateDuration';
import { formatDateFlight } from '@/utils/formatDate';
import { formatPrice } from '@/utils/formatPrice';
import { getIcon } from '@/utils/getIcon';

import {
  StyledBoardingPass,
  StyledSide,
  StyledBody,
  StyledFlight,
  StyledAirline,
  StyledAirlineLogoWrapper,
  StyledAirlineInfo,
  StyledTripWrapper,
  StyledDepartureOrArrival,
  StyledTripTiming,
  StyledTripTimingTime,
  StyledTripTimingLine,
  StyledTripTimingLineDot,
  StyledTripLayovers,
  StyledFooter,
  StyledSideRight,
} from './styles';

import { Button } from '../Button';
import { StyledIconAndText } from '../ExcursionCard/ExcursionBookingFormButton/styles';
import { CircleCheckIcon } from '../Icons';
import { Paragraph } from '../Paragraph';

import type { Flight } from '@/types/flights';
import type { Itinerary } from '@/types/itinerary';

interface BoardingPassProps {
  itinerary: Itinerary;
  outboundFlight?: Flight;
  returnFlight?: Flight;
  betweenFlight: boolean;
  selected: boolean;
  priceDifference?: number;
}

function BoardingPass({
  itinerary,
  outboundFlight,
  returnFlight,
  betweenFlight,
  selected,
  priceDifference,
}: BoardingPassProps) {
  const outboundDeparture = useMemo(
    () => (outboundFlight?.departureDateTime ? formatDateFlight(outboundFlight?.departureDateTime) : ''),
    [outboundFlight?.departureDateTime],
  );
  const outboundDuration = useMemo(
    () =>
      outboundFlight?.arrivalDateTime
        ? calculateDuration(outboundFlight?.departureDateTime, outboundFlight?.arrivalDateTime)
        : '',
    [outboundFlight?.arrivalDateTime, outboundFlight?.departureDateTime],
  );
  const outboundArrival = useMemo(
    () => (outboundFlight?.arrivalDateTime ? formatDateFlight(outboundFlight?.arrivalDateTime) : ''),
    [outboundFlight?.arrivalDateTime],
  );
  const outboundOverlay = useMemo(() => {
    if (outboundFlight?.subType === 'indirect' && outboundFlight.flights.length === 2) {
      const firstFlight = outboundFlight.flights[0];
      const secondFlight = outboundFlight.flights[1];
      const overlayTime = calculateDuration(secondFlight.departureDateTime, firstFlight.arrivalDateTime);
      return `${firstFlight.arrivalAirport} ${overlayTime}`;
    }
    return '';
  }, [outboundFlight]);

  const returnDeparture = useMemo(
    () => (returnFlight?.departureDateTime ? formatDateFlight(returnFlight?.departureDateTime) : ''),
    [returnFlight?.departureDateTime],
  );
  const returnDuration = useMemo(
    () =>
      returnFlight?.arrivalDateTime
        ? calculateDuration(returnFlight?.departureDateTime, returnFlight?.arrivalDateTime)
        : '',
    [returnFlight?.arrivalDateTime, returnFlight?.departureDateTime],
  );
  const returnArrival = useMemo(
    () => (returnFlight?.arrivalDateTime ? formatDateFlight(returnFlight?.arrivalDateTime) : ''),
    [returnFlight?.arrivalDateTime],
  );
  const returnOverlay = useMemo(() => {
    if (returnFlight?.subType === 'indirect' && returnFlight?.flights.length === 2) {
      const firstFlight = returnFlight.flights[0];
      const secondFlight = returnFlight.flights[1];
      const overlayTime = calculateDuration(secondFlight.departureDateTime, firstFlight.arrivalDateTime);
      return `${firstFlight.arrivalAirport} ${overlayTime}`;
    }
    return '';
  }, [returnFlight]);

  const priceDifferencePerPerson = useMemo(() => {
    if (priceDifference) {
      const formattedPriceDifference = formatPrice(priceDifference / itinerary.amountOfTravelers);
      return priceDifference > 0 ? `+${formattedPriceDifference}` : formattedPriceDifference;
    }
    return 0;
  }, [priceDifference, itinerary.amountOfTravelers]);

  return (
    <StyledBoardingPass $betweenFlight={betweenFlight}>
      <StyledSide />
      <StyledBody $betweenFlight={betweenFlight}>
        <StyledFlight $side="left" $betweenFlight={betweenFlight}>
          <StyledAirline>
            <StyledAirlineLogoWrapper>
              <img
                src="https://global.discourse-cdn.com/infiniteflight/original/3X/c/0/c0bd00655c561aa489f269af91bb67da98f37a7e.png"
                alt="Airline Logo"
                width="100%"
                height="100%"
              />
            </StyledAirlineLogoWrapper>
            <StyledAirlineInfo>
              {!betweenFlight && (
                <Paragraph size="sm" weight={700}>
                  Outward flight
                </Paragraph>
              )}
              <Paragraph size="xl" weight={700}>
                {outboundFlight?.supplier}
              </Paragraph>
              <Paragraph size="sm" weight={600} color="primary">
                {outboundFlight?.class} class, {itinerary.amountOfTravelers} adults
              </Paragraph>
            </StyledAirlineInfo>
          </StyledAirline>
          <StyledTripWrapper>
            <StyledDepartureOrArrival>
              <Paragraph size="sm" weight={500} color="primary">
                Departure
              </Paragraph>
              <Paragraph size="md" weight={700}>
                {outboundFlight?.departureAirport}
              </Paragraph>
              <Paragraph size="sm" weight={500}>
                {outboundDeparture}
              </Paragraph>
            </StyledDepartureOrArrival>
            <StyledTripTiming>
              <StyledTripTimingTime>
                <Paragraph color="primary" size="xs" weight={700}>
                  {getIcon('clock', 'lg')}
                  {outboundDuration}
                </Paragraph>
              </StyledTripTimingTime>
              <StyledTripTimingLine>
                {outboundFlight?.subType === 'indirect'
                  ? [1, 2, 3].map((item, index) => <StyledTripTimingLineDot $totalDots={3} $index={index} key={item} />)
                  : [1, 2].map((item, index) => <StyledTripTimingLineDot $totalDots={2} $index={index} key={item} />)}
              </StyledTripTimingLine>
              {outboundFlight?.subType === 'indirect' && (
                <StyledTripLayovers>
                  <Paragraph size="xs" color="primary" weight={600}>
                    {outboundOverlay}
                  </Paragraph>
                </StyledTripLayovers>
              )}
            </StyledTripTiming>
            <StyledDepartureOrArrival>
              <Paragraph size="sm" weight={500} color="primary">
                Arrival
              </Paragraph>
              <Paragraph size="md" weight={700}>
                {outboundFlight?.arrivalAirport}
              </Paragraph>
              <Paragraph size="sm" weight={500}>
                {outboundArrival}
              </Paragraph>
            </StyledDepartureOrArrival>
          </StyledTripWrapper>
          <StyledFooter>
            {betweenFlight && !selected && priceDifference !== 0 && (
              <Button variant="secondary">
                <Paragraph>Select {priceDifferencePerPerson} P.P.</Paragraph>
              </Button>
            )}
            {betweenFlight && selected && (
              <Button variant="secondaryBright" onClick={() => {}}>
                <StyledIconAndText>
                  <CircleCheckIcon size="lg" /> Selected
                </StyledIconAndText>
              </Button>
            )}
          </StyledFooter>
        </StyledFlight>

        {!betweenFlight && (
          <StyledFlight $side="right" $betweenFlight={betweenFlight}>
            <StyledAirline>
              <StyledAirlineLogoWrapper>
                <img
                  src="https://global.discourse-cdn.com/infiniteflight/original/3X/c/0/c0bd00655c561aa489f269af91bb67da98f37a7e.png"
                  alt="Airline Logo"
                  width="100%"
                  height="100%"
                />
              </StyledAirlineLogoWrapper>
              <StyledAirlineInfo>
                <Paragraph size="sm" weight={700}>
                  Return flight
                </Paragraph>
                <Paragraph size="xl" weight={700}>
                  {returnFlight?.supplier}
                </Paragraph>
                <Paragraph size="sm" weight={600} color="primary">
                  {outboundFlight?.class} class, {itinerary.amountOfTravelers} adults
                </Paragraph>
              </StyledAirlineInfo>
            </StyledAirline>
            <StyledTripWrapper>
              <StyledDepartureOrArrival>
                <Paragraph size="sm" weight={500} color="primary">
                  Departure
                </Paragraph>
                <Paragraph size="md" weight={700}>
                  {returnFlight?.departureAirport}
                </Paragraph>
                <Paragraph size="sm" weight={500}>
                  {returnDeparture}
                </Paragraph>
              </StyledDepartureOrArrival>
              <StyledTripTiming>
                <StyledTripTimingTime>
                  <Paragraph color="primary" size="xs" weight={700}>
                    {getIcon('clock', 'lg')}
                    {returnDuration}
                  </Paragraph>
                </StyledTripTimingTime>
                <StyledTripTimingLine>
                  {returnFlight?.subType === 'indirect'
                    ? [1, 2, 3].map((item, index) => (
                        <StyledTripTimingLineDot $totalDots={3} $index={index} key={item} />
                      ))
                    : [1, 2].map((item, index) => <StyledTripTimingLineDot $totalDots={2} $index={index} key={item} />)}
                </StyledTripTimingLine>
                {returnFlight?.subType === 'indirect' && (
                  <StyledTripLayovers>
                    <Paragraph size="xs" color="primary" weight={600}>
                      {returnOverlay}
                    </Paragraph>
                  </StyledTripLayovers>
                )}
              </StyledTripTiming>
              <StyledDepartureOrArrival>
                <Paragraph size="sm" weight={500} color="primary">
                  Arrival
                </Paragraph>
                <Paragraph size="md" weight={700}>
                  {returnFlight?.arrivalAirport}
                </Paragraph>
                <Paragraph size="sm" weight={500}>
                  {returnArrival}
                </Paragraph>
              </StyledDepartureOrArrival>
            </StyledTripWrapper>
            <StyledFooter>
              {!selected && priceDifference !== 0 && (
                <Button variant="secondary">
                  <Paragraph>Select {priceDifferencePerPerson} P.P.</Paragraph>
                </Button>
              )}
              {selected && (
                <Button variant="secondaryBright" onClick={() => {}}>
                  <StyledIconAndText>
                    <CircleCheckIcon size="lg" /> Selected
                  </StyledIconAndText>
                </Button>
              )}
            </StyledFooter>
          </StyledFlight>
        )}
      </StyledBody>
      <StyledSideRight />
    </StyledBoardingPass>
  );
}

export { BoardingPass };
